import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider, useAppFont } from './contexts/SettingsContext';
import ProtectedRoute from './components/ProtectedRoute';
import MosqueShell from './components/MosqueShell';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminPanel from './pages/SuperAdminPanel';
import TakmirRegisterPage from './pages/TakmirRegisterPage';
import MerchantRegisterPage from './pages/MerchantRegisterPage';
import MerchantDashboard from './pages/MerchantDashboard';
import JamaahDashboard from './pages/JamaahDashboard';
import KemenagDashboard from './pages/KemenagDashboard';
import UPZDashboard from './pages/UPZDashboard';
import DaftarUPZ from './pages/DaftarUPZ';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminInner() {
  useAppFont();
  return <AdminDashboard />;
}

function AdminShell() {
  const { user } = useAuth();
  return (
    <SettingsProvider mosqueId={user?.mosque_id}>
      <AdminInner />
    </SettingsProvider>
  );
}

// Handles the Emergent Google OAuth return (#session_id=...) exactly once.
function AuthCallback() {
  const navigate = useNavigate();
  const { processGoogleSession } = useAuth();
  const processed = React.useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sid = match ? decodeURIComponent(match[1]) : null;
    (async () => {
      if (!sid) return navigate('/login', { replace: true });
      const res = await processGoogleSession(sid);
      window.history.replaceState(null, '', window.location.pathname);
      if (res.success) {
        const returnPath = localStorage.getItem('auth_return');
        localStorage.removeItem('auth_return');
        if (returnPath && returnPath.startsWith('/region/')) {
          navigate(returnPath, { replace: true });
        } else {
          const role = res.user?.role;
          navigate(
            role === 'super_admin' ? '/superadmin' :
            role === 'takmir' ? '/admin' :
            role === 'merchant' ? '/merchant' :
            (role === 'kemenag_kab' || role === 'kemenag_prov') ? '/kemenag' :
            role === 'upz' ? '/upz' : '/jamaah',
            { replace: true }
          );
        }
      } else {
        navigate('/login?err=google', { replace: true });
      }
    })();
  }, [navigate, processGoogleSession]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#022C22] text-white" data-testid="auth-callback">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-amber-400 mb-4" />
      <p className="font-body text-sm">Memproses login Google…</p>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/daftar-masjid" element={<TakmirRegisterPage />} />
      <Route path="/daftar-umkm" element={<MerchantRegisterPage />} />
      <Route
        path="/merchant"
        element={
          <ProtectedRoute roles={['merchant']}>
            <MerchantDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jamaah"
        element={
          <ProtectedRoute roles={['user', 'merchant', 'takmir', 'super_admin']}>
            <JamaahDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/daftar-upz" element={<DaftarUPZ />} />
      <Route
        path="/upz"
        element={
          <ProtectedRoute roles={['upz', 'super_admin']}>
            <UPZDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kemenag"
        element={
          <ProtectedRoute roles={['kemenag_kab', 'kemenag_prov', 'super_admin']}>
            <KemenagDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/superadmin"
        element={
          <ProtectedRoute roles={['super_admin']}>
            <SuperAdminPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminShell />
          </ProtectedRoute>
        }
      />
      <Route path="/region/:regencySlug/:villageSlug/:mosqueSlug/*" element={<MosqueShell />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.style.setProperty('--app-font', `'Outfit'`);
    axios.get(`${API_URL}/api/platform-settings`)
      .then(({ data }) => {
        if (data.splash_title) localStorage.setItem('splash_title', data.splash_title);
        if (data.splash_tagline) localStorage.setItem('splash_tagline', data.splash_tagline);
        document.title = data.splash_title
          ? `${data.splash_title} — ${data.splash_tagline || ''}`.trim().replace(/—\s*$/, '')
          : 'Masjid Berdaya';
      })
      .catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-[#F8F9FA]">
          <AppRoutes />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
